import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { ReactSVG } from 'react-svg';
import StateContext from '../../contexts/StateContext';
import { Listbox, ListboxInput, ListboxButton, ListboxPopover, ListboxList, ListboxOption } from '@reach/listbox';
import '@reach/listbox/styles.css';
import { deviceKindInactiveMap } from '../../constants';
import useMediaCoreContext from '../MediaCoreProvider/useMediaCoreContext/useMediaCoreContext';

const getDeviceList = (allDevices, kind, deviceId) => {
	// Iterate over a list of devices for a given input type (or kind)
	// If no active device is present for this input type, insert a default
	// of none selected.
	let devices = allDevices || [];

	if (kind === 'videoinput' || kind === 'audioinput') {
		const inactiveDevice = {
			deviceId: deviceKindInactiveMap[kind].deviceId,
			label: deviceKindInactiveMap[kind].label,
		};
		devices = [...devices, inactiveDevice];
	}

	return devices.map((device) => {
		if (device.label === '') return;
		return (
			<ListboxOption
				key={device.deviceId}
				value={device.deviceId}
				className={'p-2 cursor-pointer flex justify-between text-sm'}
			>
				<span> {device.label}</span>
				{device.deviceId === deviceId && (
					<ReactSVG className={'fill-current inline-block w-4'} src={`../../../images/icons/check.svg`} />
				)}
			</ListboxOption>
		);
	});
};

const Select = (props) => {
	const { devices, activeDevices, setActiveDevice } = useMediaCoreContext();

	const { deviceId, label } = activeDevices[props.kind] || deviceKindInactiveMap[props.kind];
	const [selectedDevice, setSelectedDevice] = useState({ deviceId, label });
	const [changingDevice, setChangingDevice] = useState(false);

	const kindDevices = devices.filter((device) => {
		return device.kind === props.kind;
	});

	const handleDeviceSelected = (selectedDeviceId) => {
		if (!changingDevice && selectedDeviceId !== deviceId) {
			setChangingDevice(true);
			const deviceLabel =
				devices.find((d) => d.deviceId === selectedDeviceId && d.kind === props.kind)?.label ||
				deviceKindInactiveMap[props.kind].label;
			setSelectedDevice({ deviceId: selectedDeviceId, label: deviceLabel });
			setActiveDevice({ deviceId: selectedDeviceId, kind: props.kind });
		}
	};

	useEffect(() => {
		const deviceLabel =
			devices.find((d) => d.deviceId === deviceId && d.kind === props.kind)?.label ||
			deviceKindInactiveMap[props.kind].label;
		setSelectedDevice({ deviceId: deviceId, label: deviceLabel });
		setChangingDevice(false);
	}, [deviceId]);

	return (
		<>
			<label className={'sr-only'} htmlFor={props.kind}>
				Choose your {props.kind}
			</label>

			<ListboxInput
				disabled={props.disabled}
				aria-labelledby={props.kind}
				className={'mt-2 bg-darkEbony text-ivory w-full py-1 rounded'}
				name={props.kind}
				data-test-id={`${props.kind}-listbox`}
				value={selectedDevice.deviceId || ''}
				onChange={(newDeviceId) => {
					handleDeviceSelected(newDeviceId);
				}}
			>
				<ListboxButton className={'p-0 flex truncate items-center pr-2 min-w-0 text-sm border-none '}>
					{props.kind === 'audioinput' ? (
						<div className={`relative h-10 w-8 mr-5 rounded`}>
							<canvas
								style={{ bottom: 0, left: 3 }}
								className={`h-10 w-10 absolute bg-jetGrey rounded ${
									selectedDevice.label === 'mic_off' ? 'hidden' : ''
								}`}
								id={'VolumeMeter'}
							></canvas>
							<ReactSVG
								style={{ top: 10, left: 12 }}
								className={`${
									selectedDevice.label === 'mic_off'
										? 'fill-current inline-block p-2 bg-jetGrey ml-1 mr-2 rounded'
										: 'fill-current absolute'
								}`}
								src={`../../../images/icons/${props.kind}.svg`}
								loading={() => <div className={'h-6 w-6 bg-jetGrey'}></div>}
							/>
						</div>
					) : (
						<ReactSVG
							className={'fill-current inline-block p-2 bg-jetGrey ml-1 mr-2 rounded'}
							src={`../../../images/icons/${props.kind}.svg`}
							loading={() => <div className={'h-6 w-6 bg-jetGrey'}></div>}
						/>
					)}
					<span className={'truncate '}>{selectedDevice.label}</span>
					<ReactSVG
						className={'fill-current inline-block'}
						style={{ marginLeft: 'auto' }}
						src={'../../../images/icons/expand_more.svg'}
					/>
				</ListboxButton>
				<ListboxPopover className={'mt-2 rounded shadow-lg bg-darkGrey p-0'}>
					<ListboxList className={'bg-darkGrey text-ivory p-0'}>
						{getDeviceList(kindDevices, props.kind, selectedDevice)}
					</ListboxList>
				</ListboxPopover>
			</ListboxInput>
		</>
	);
};

const DevicesSelector = (props) => {
	return (
		<>
			<DeviceSelector
				kind={'audioinput'}
				disabled={props.disabled}
				onDeviceChange={props.onDeviceChange}
				hiddenSelectors={props.hiddenSelectors}
			/>
			<DeviceSelector
				kind={'videoinput'}
				disabled={props.disabled}
				onDeviceChange={props.onDeviceChange}
				hiddenSelectors={props.hiddenSelectors}
			/>
			<DeviceSelector
				kind={'audiooutput'}
				disabled={props.disabled}
				onDeviceChange={props.onDeviceChange}
				hiddenSelectors={props.hiddenSelectors}
			/>
		</>
	);
};

const DeviceSelector = ({ kind, disabled, onDeviceChange, hiddenSelectors }) => {
	if (hiddenSelectors && hiddenSelectors.includes(kind)) {
		return null;
	}
	return <Select kind={kind} disabled={disabled} onDeviceChange={onDeviceChange} />;
};

export default DevicesSelector;
